"use client";

import { ArrowLeft, ExternalLink } from "lucide-react";
import type { ComponentSchema } from "@/lib/ecoBuilder/schema/types";
import { cn } from "@/lib/utils";

// ═══════════════════════════════════════════════════════════════
// ButtonAtom - Button Building Block
// ═══════════════════════════════════════════════════════════════

/**
 * Props interface for ButtonAtom
 */
export interface ButtonAtomProps {
    label: string;
    link: string;
    variant: "primary" | "secondary" | "outline" | "ghost";
    size: "sm" | "md" | "lg";
    icon: "arrow" | "external" | "none";
    fullWidth: boolean;
    backgroundColor: string;
    textColor: string;
    style?: React.CSSProperties;
    className?: string;
    onUpdateProps?: (props: Record<string, unknown>) => void;
}

/**
 * THE SCHEMA - Explicit definition for the inspector
 */
export const ButtonAtomSchema: ComponentSchema = {
    label: {
        type: "text",
        label: "النص",
        placeholder: "اضغط هنا",
        defaultValue: "اضغط هنا",
    },
    link: {
        type: "url",
        label: "الرابط",
        placeholder: "https://...",
        defaultValue: "#",
    },
    variant: {
        type: "select",
        label: "النمط",
        defaultValue: "primary",
        options: [
            { value: "primary", label: "رئيسي" },
            { value: "secondary", label: "ثانوي" },
            { value: "outline", label: "إطار" },
            { value: "ghost", label: "شفاف" },
        ],
    },
    size: {
        type: "select",
        label: "الحجم",
        defaultValue: "md",
        options: [
            { value: "sm", label: "صغير" },
            { value: "md", label: "متوسط" },
            { value: "lg", label: "كبير" },
        ],
    },
    icon: {
        type: "select",
        label: "الأيقونة",
        defaultValue: "arrow",
        options: [
            { value: "arrow", label: "سهم" },
            { value: "external", label: "رابط خارجي" },
            { value: "none", label: "بدون" },
        ],
    },
    fullWidth: {
        type: "boolean",
        label: "عرض كامل",
        defaultValue: false,
    },
    backgroundColor: {
        type: "color",
        label: "لون الخلفية (اختياري)",
        defaultValue: "",
        description: "يتجاوز لون النمط الافتراضي",
    },
    textColor: {
        type: "color",
        label: "لون النص (اختياري)",
        defaultValue: "",
        description: "يتجاوز لون النص الافتراضي",
    },
};

/**
 * Default props
 */
export const ButtonAtomDefaultProps = {
    label: "اضغط هنا",
    link: "#",
    variant: "primary" as const,
    size: "md" as const,
    icon: "arrow" as const,
    fullWidth: false,
    backgroundColor: "",
    textColor: "",
} satisfies ButtonAtomProps;

const ICON_MAP = {
    arrow: <ArrowLeft className="h-4 w-4" />,
    external: <ExternalLink className="h-4 w-4" />,
    none: null,
};

const SIZE_CLASSES = {
    sm: "h-9 px-4 text-sm",
    md: "h-11 px-6 text-base",
    lg: "h-14 px-8 text-lg",
};

const VARIANT_CLASSES = {
    primary: "bg-gradient-to-l from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700 shadow-lg shadow-violet-500/25",
    secondary: "bg-slate-900 text-white hover:bg-slate-800",
    outline: "border-2 border-slate-300 bg-transparent text-slate-900 hover:bg-slate-50",
    ghost: "bg-transparent text-slate-700 hover:bg-slate-100",
};

/**
 * The ButtonAtom Component
 */
export function ButtonAtom({
    label = ButtonAtomDefaultProps.label,
    link = ButtonAtomDefaultProps.link,
    variant = ButtonAtomDefaultProps.variant,
    size = ButtonAtomDefaultProps.size,
    icon = ButtonAtomDefaultProps.icon,
    fullWidth = ButtonAtomDefaultProps.fullWidth,
    backgroundColor = ButtonAtomDefaultProps.backgroundColor,
    textColor = ButtonAtomDefaultProps.textColor,
    style,
    className,
}: Partial<ButtonAtomProps>) {
    const customStyles: React.CSSProperties = {
        ...(backgroundColor ? { backgroundColor } : {}),
        ...(textColor ? { color: textColor } : {}),
        ...style,
    };

    return (
        <a
            href={link}
            className={cn(
                "inline-flex items-center justify-center gap-2 font-bold rounded-xl transition-all duration-200 active:scale-95",
                SIZE_CLASSES[size],
                (!backgroundColor && !style?.backgroundColor) ? VARIANT_CLASSES[variant] : "",
                fullWidth ? "w-full" : "",
                className
            )}
            style={customStyles}
        >
            {label}
            {ICON_MAP[icon]}
        </a>
    );
}

export default ButtonAtom;
